import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import type { ChatComposerHandle } from "@/features/data-curation/components/CurationChatPanel";
import { UnsentChatMessage } from "@/features/data-curation/components/UnsentChatMessage";
import { useStartExtractionCollection } from "@/features/data-curation/hooks/useCuration";
import { useCurationAgentMessage } from "@/features/data-curation/hooks/useCurationAgentMessage";
import { startCollectionMessage, withUnsentMessage } from "@/features/data-curation/schemaDraft";
import type { ExtractionDataset } from "@/features/data-curation/types";
import { Play } from "lucide-react";
import { useState, type RefObject } from "react";
import { toast } from "sonner";

const startError = (error: unknown) =>
  error instanceof Error && error.message ? error.message : "The collection could not start.";

/**
 * Shown while the columns are approved and the collection waits for the person,
 * so nobody waits for a collection that has not started.
 */
export const StartCollectionBar = ({
  dataset,
  agentWorking,
  composer,
  onStarted,
}: {
  dataset: ExtractionDataset;
  agentWorking: boolean;
  /** The chat box, whose unsent message can go with the start. */
  composer: RefObject<ChatComposerHandle | null>;
  onStarted?: () => void;
}) => {
  const startCollection = useStartExtractionCollection(dataset.id);
  const messageAgent = useCurationAgentMessage(dataset.chat_session_id);
  // The unsent message while the dialog asks about it; null when the dialog is closed.
  const [unsent, setUnsent] = useState<string | null>(null);
  const [sendUnsent, setSendUnsent] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasChat = Boolean(dataset.chat_session_id);

  const start = async (message: string, send: boolean) => {
    setError(null);
    try {
      await startCollection.mutateAsync();
    } catch (failure) {
      setError(startError(failure));
      return;
    }
    const sendsUnsent = send && Boolean(message);
    const startMessage = startCollectionMessage(dataset.id);
    messageAgent(sendsUnsent ? withUnsentMessage(message, startMessage) : startMessage);
    if (sendsUnsent) composer.current?.clear();
    setUnsent(null);
    onStarted?.();
    toast.success("The agent starts the collection in the chat.");
  };

  const clickStart = () => {
    const message = composer.current?.unsentMessage() ?? "";
    if (!message) {
      void start("", false);
      return;
    }
    setError(null);
    setSendUnsent(true);
    setUnsent(message);
  };

  return (
    <section
      aria-label="Collection not started"
      className="@container rounded-[3px] border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm"
    >
      <div className="flex flex-col gap-3 @2xl:flex-row @2xl:items-center @2xl:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="flex flex-wrap items-center gap-2 font-medium text-zinc-900">
            <Badge>Not started</Badge>
            The columns are approved, but the collection has not started.
          </p>
          <p className="text-zinc-600">
            {hasChat
              ? "Talk with the agent in the chat about the scope of the run. When you are ready, click Start collection."
              : "Ask your agent to start the collection."}
          </p>
          {hasChat && agentWorking && (
            <p className="text-zinc-600" aria-live="polite">
              The agent is working. You can start the collection when it stops.
            </p>
          )}
          {error && unsent === null && (
            <p className="text-red-700" role="alert">
              {error}
            </p>
          )}
        </div>
        {hasChat && (
          <Button
            size="sm"
            className="self-start @2xl:shrink-0 @2xl:self-auto"
            onClick={clickStart}
            loading={startCollection.isPending && unsent === null}
            disabled={agentWorking}
          >
            <Play className="h-4 w-4" aria-hidden />
            Start collection
          </Button>
        )}
      </div>
      <Dialog
        open={unsent !== null}
        onOpenChange={(next) => {
          if (!next && !startCollection.isPending) setUnsent(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start the collection?</DialogTitle>
            <DialogDescription>The agent starts the collection in the chat.</DialogDescription>
          </DialogHeader>
          <UnsentChatMessage
            text={unsent ?? ""}
            send={sendUnsent}
            disabled={startCollection.isPending}
            onSendChange={setSendUnsent}
          />
          {error && (
            <p className="mt-2 rounded-[3px] bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUnsent(null)}
              disabled={startCollection.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void start(unsent ?? "", sendUnsent)}
              loading={startCollection.isPending}
              disabled={agentWorking}
            >
              <Play className="h-4 w-4" aria-hidden />
              Start collection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
